'use client';

import {useEffect, useState} from 'react';
import {motion, AnimatePresence} from 'framer-motion';
import {useVideoPlayer} from '../hooks/useVideoPlayer';
import VideoControls from './VideoControls';
import {VideoItemProps} from '@/data/types';
import {useData} from '@/i18n/LangContext';

export default function VideoItem({videoId, title}: VideoItemProps) {
  const {ui} = useData();
  const [isHovering, setIsHovering] = useState(false);
  const [controlsVisible, setControlsVisible] = useState(false);
  const [videoLoading, setVideoLoading] = useState(true);
  const [showSubtitles, setShowSubtitles] = useState(true);

  const {
    videoRef,
    isPlaying,
    progress,
    volume,
    isVideoReady,
    togglePlay,
    handleTimeUpdate,
    handleVolumeChange,
    handleSeek,
  } = useVideoPlayer();

  const toggleSubtitles = () => {
    setShowSubtitles(!showSubtitles);
  };

  // Pfade zu den Videodateien
  const videoPath = `/assets/videos/${videoId}/${videoId}.mp4`;
  const vttPath = `/assets/videos/${videoId}/${videoId}.vtt`;

  // Steuerung der Anzeige/Ausblendung der Steuerelemente mit Verzögerung
  useEffect(() => {
    let hideTimeout: NodeJS.Timeout;

    const showControls = () => {
      clearTimeout(hideTimeout);
      setControlsVisible(true);
    };

    const hideControls = () => {
      hideTimeout = setTimeout(() => {
        if (!isHovering && !isPlaying) {
          setControlsVisible(false);
        }
      }, 500);
    };

    if (isHovering || isPlaying) {
      showControls();

      if (isPlaying && !isHovering) {
        hideTimeout = setTimeout(() => {
          setControlsVisible(false);
        }, 1500);
      }
    } else {
      hideControls();
    }

    return () => {
      clearTimeout(hideTimeout);
    };
  }, [isHovering, isPlaying]);

  // Überwachung des Videostatus
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleLoadStart = () => setVideoLoading(true);
    const handleCanPlay = () => setVideoLoading(false);

    video.addEventListener('loadstart', handleLoadStart);
    video.addEventListener('canplay', handleCanPlay);

    return () => {
      video.removeEventListener('loadstart', handleLoadStart);
      video.removeEventListener('canplay', handleCanPlay);
    };
  }, [videoRef]);

  return (
    <div
      className="relative"
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      {/* Video-Container mit overflow-hidden */}
      <motion.div
        className="rounded-lg bg-gray-900 border border-gray-800 shadow-xl"
        initial={{opacity: 0, y: 20}}
        animate={{opacity: 1, y: 0}}
        transition={{duration: 0.5}}
      >
        <div className="relative overflow-hidden rounded-lg">
          {/* Video-Loader anzeigen, solange das Video lädt */}
          {videoLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-900 z-20">
              <div className="size-12 border-3 border-[#ff88ff] border-t-transparent rounded-full animate-spin"></div>
            </div>
          )}

          <video
            ref={videoRef}
            className="w-full h-auto"
            onTimeUpdate={handleTimeUpdate}
            onClick={togglePlay}
            preload="metadata" // Nur Metadaten vorab laden
          >
            <source src={videoPath} type="video/mp4" />
            {showSubtitles && <track
              default
              kind="subtitles"
              srcLang="en"
              src={vttPath}
            />}
            {ui.media.videoUnsupported}
          </video>

          {!isPlaying && (
            <div className="absolute top-0 left-0 right-0 bg-black/60 p-2 text-white z-10">
              {title}
            </div>
          )}

          {/* Play-Button in der Mitte wenn nicht abspielend */}
          {!isPlaying && isVideoReady && (
            <motion.div
              className="absolute inset-0 flex items-center justify-center pointer-events-none"
              initial={{opacity: 0}}
              animate={{opacity: 1}}
              exit={{opacity: 0}}
            >
              <div className="bg-black/40 rounded-full p-4 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                     strokeLinecap="round" strokeLinejoin="round" className="text-white">
                  <polygon points="5 3 19 12 5 21 5 3"></polygon>
                </svg>
              </div>
            </motion.div>
          )}
        </div>
      </motion.div>

      {/* Video Controls - AUSSERHALB des overflow-hidden Video-Containers */}
      <AnimatePresence>
        {controlsVisible && (
          <VideoControls
            isPlaying={isPlaying}
            progress={progress}
            volume={volume}
            showSubtitles={showSubtitles}
            onTogglePlay={togglePlay}
            onToggleSubtitles={toggleSubtitles}
            onVolumeChange={handleVolumeChange}
            onSeek={handleSeek}
          />
        )}
      </AnimatePresence>
    </div>
  );
}